import { z } from "zod";
import { MediaSchema } from "./media";

export const PartnerBadgeListingTypeSchema = z.enum(['app', 'theme']);

export const PartnerBadgeListingSchema = z.object({
    type: PartnerBadgeListingTypeSchema,
    url: z.string(),
    name: z.string(),
    tagline: z.string().nullable().optional(),
    developer_name: z.string().nullable().optional(),
    icon_url: z.string().nullable().optional(),
    rating: z.number().nullable().optional(),
    review_count: z.number().nullable().optional(),
    positive_review_percent: z.number().nullable().optional(),
    pricing_label: z.string().nullable().optional(),
    price_amount: z.number().nullable().optional(),
    price_currency: z.string().nullable().optional(),
    built_for_shopify: z.boolean().nullable().optional(),
    synced_at: z.string().nullable().optional(),
});

// A malformed listing must never fail the whole badge (and the page that resolves it).
const OptionalPartnerBadgeListingSchema = PartnerBadgeListingSchema.nullable().optional().catch(null);

export const PartnerBadgeListingSyncSchema = z.object({
    status: z.enum(['none', 'pending', 'synced', 'failed']),
    last_synced_at: z.string().nullable().optional(),
    last_attempted_at: z.string().nullable().optional(),
    error: z.object({
        code: z.string(),
        message: z.string(),
    }).nullable().optional(),
});

// Response schema - badge with full icon media object
export const PartnerBadgeSchema = z.object({
    id: z.number(),
    name: z.string(),
    slug: z.string(),
    status: z.enum(['active', 'inactive']),
    tag: z.string().nullable().optional(),
    expert_count: z.number().optional().default(0),
    sort_index: z.number(),
    icon: MediaSchema.nullable(),
    listing_url: z.string().nullable().optional(),
    listing_type: PartnerBadgeListingTypeSchema.nullable().optional(),
    listing_auto_update: z.boolean().optional().default(false),
    listing: OptionalPartnerBadgeListingSchema,
    listing_sync: PartnerBadgeListingSyncSchema.nullable().optional().catch(null),
    created_at: z.string(),
    updated_at: z.string(),
});

// Request schema for creating a badge
export const CreateBadgeRequestSchema = z.object({
    name: z.string(),
    slug: z.string().optional(),
    icon_id: z.string().optional(), // ULID for media
    status: z.enum(['active', 'inactive']).optional(),
    tag: z.string().nullable().optional(),
    listing_url: z.string().nullable().optional(),
    listing_auto_update: z.boolean().optional(),
});

// Request schema for updating a badge
export const UpdateBadgeRequestSchema = z.object({
    name: z.string().optional(),
    slug: z.string().optional(),
    icon_id: z.string().optional(),
    status: z.enum(['active', 'inactive']).optional(),
    tag: z.string().nullable().optional(),
    listing_url: z.string().nullable().optional(),
    listing_auto_update: z.boolean().optional(),
});

// Request schema for syncing badges to an expert
export const SyncBadgesToExpertRequestSchema = z.object({
    badge_ids: z.array(z.number()),
});

// Request schema for reordering expert badges
export const ReorderBadgesRequestSchema = z.object({
    badge_ids: z.array(z.number()),
});

// Response schema for sync badges to expert
export const SyncBadgesResponseSchema = z.object({
    expert_id: z.number(),
    badges: z.array(PartnerBadgeSchema),
});

// Public badge schema - simplified for public API
export const PublicPartnerBadgeSchema = z.object({
    name: z.string(),
    slug: z.string(),
    tag: z.string().nullable().optional(),
    expert_count: z.number().optional().default(0),
    href: z.string().nullable().optional(),
    icon_url: z.string().nullable(),
    listing: OptionalPartnerBadgeListingSchema,
});

// Public badge lookup by stable database ID. Unlike the legacy slug lookup,
// this payload is complete enough for consumers to render and link a badge
// without storing presentation metadata in page configuration.
export const PublicPartnerBadgeByIdSchema = PublicPartnerBadgeSchema.extend({
    id: z.number().int().positive(),
    href: z.string().min(1),
});

export const PublicPartnerBadgeGroupSchema = z.object({
    tag: z.string(),
    expert_count: z.number(),
    badges: z.array(PublicPartnerBadgeSchema),
});

// TypeScript type exports
export type PartnerBadge = z.infer<typeof PartnerBadgeSchema>;
export type PartnerBadgeListing = z.infer<typeof PartnerBadgeListingSchema>;
export type PartnerBadgeListingType = z.infer<typeof PartnerBadgeListingTypeSchema>;
export type PartnerBadgeListingSync = z.infer<typeof PartnerBadgeListingSyncSchema>;
export type PublicPartnerBadge = z.infer<typeof PublicPartnerBadgeSchema>;
export type PublicPartnerBadgeById = z.infer<typeof PublicPartnerBadgeByIdSchema>;
export type PublicPartnerBadgeGroup = z.infer<typeof PublicPartnerBadgeGroupSchema>;
export type CreateBadgeRequest = z.infer<typeof CreateBadgeRequestSchema>;
export type UpdateBadgeRequest = z.infer<typeof UpdateBadgeRequestSchema>;
export type SyncBadgesToExpertRequest = z.infer<typeof SyncBadgesToExpertRequestSchema>;
export type ReorderBadgesRequest = z.infer<typeof ReorderBadgesRequestSchema>;
export type SyncBadgesResponse = z.infer<typeof SyncBadgesResponseSchema>;
